import { useTranslation } from 'react-i18next'
import { Clock, Download } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table'
import { Button } from '@/components/shadcn/button'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import type { EmployeeUserResponse } from '@/api/model'
import type { TimeTrackingSummaryState } from '../hooks/useTimeTrackingSummaries'

interface Props {
  employees: EmployeeUserResponse[]
  summariesById: Map<string, TimeTrackingSummaryState>
  isLoading: boolean
  isError: boolean
  onRowClick?: (employee: EmployeeUserResponse) => void
  /** Row button "show work-time quota". */
  onShowQuota?: (employee: EmployeeUserResponse) => void
  /** Row button "export time tracking". */
  onExport?: (employee: EmployeeUserResponse) => void
}

/** 6 data columns + 2 columns for the row buttons. */
const COL_COUNT = 8

/** Whole number without a decimal place, otherwise with one decimal place. */
function formatHours(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

/**
 * Dumb table of the time-tracking overview: renders only rows + row buttons and
 * reports clicks upward via callbacks (dialog/view state lives in the caller
 * `TimeTrackingOverviewTable`). Pattern like `AbsenceOverviewDataTable`.
 */
export function TimeTrackingOverviewDataTable({
  employees,
  summariesById,
  isLoading,
  isError,
  onRowClick,
  onShowQuota,
  onExport,
}: Props) {
  const { t } = useTranslation(['service-modules-timetracking', 'common'])
  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-card shadow-sm [&_th]:bg-card">
        <TableRow>
          <TableHead>{t('columns.firstName', { ns: 'common' })}</TableHead>
          <TableHead>{t('columns.lastName', { ns: 'common' })}</TableHead>
          <TableHead className="text-right">{t('timetracking.overview.worked')}</TableHead>
          <TableHead className="text-right">{t('timetracking.overview.planned')}</TableHead>
          <TableHead className="text-right">{t('timetracking.overview.balance')}</TableHead>
          <TableHead className="text-right">{t('timetracking.overview.pendingApprovals')}</TableHead>
          <TableHead className="w-10" />
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        <LoaderTable
          isLoading={isLoading}
          isError={isError}
          isEmpty={employees.length === 0}
          colSpan={COL_COUNT}
          errorMessage={t('timetracking.overview.loadError')}
          emptyMessage={t('timetracking.overview.empty')}
        >
          {employees.map((employee) => {
          const state = employee.id ? summariesById.get(employee.id) : undefined
          const summary = state?.summary

          /** Hour-value cell: loading dots during the fan-out, "—" when there's no value. */
          const hourCell = (value: number | null | undefined) => {
            if (state?.isPending) return '…'
            if (state?.isError || value == null) return '—'
            return t('timetracking.overview.hours', { hours: formatHours(value) })
          }

          return (
            <TableRow
              key={employee.id}
              onClick={onRowClick ? () => onRowClick(employee) : undefined}
              className={onRowClick ? 'cursor-pointer' : undefined}
            >
              <TableCell>{employee.firstName || '—'}</TableCell>
              <TableCell>{employee.lastName || '—'}</TableCell>
              <TableCell className="text-right tabular-nums">{hourCell(summary?.workedHours)}</TableCell>
              <TableCell className="text-right tabular-nums">{hourCell(summary?.plannedHours)}</TableCell>
              <TableCell
                className={
                  'text-right tabular-nums ' +
                  (summary?.balanceHours == null
                    ? ''
                    : summary.balanceHours >= 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-600 dark:text-red-400')
                }
              >
                {hourCell(summary?.balanceHours)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {state?.isPending ? '…' : summary?.pendingApprovals ?? '—'}
              </TableCell>
              {/* rowButton1: show work-time quota */}
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('timetracking.overview.showQuota')}
                  title={t('timetracking.overview.showQuota')}
                  onClick={() => onShowQuota?.(employee)}
                >
                  <Clock className="size-4 text-primary" />
                </Button>
              </TableCell>
              {/* rowButton2: export time tracking */}
              <TableCell onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('timetracking.overview.exportRow')}
                  title={t('timetracking.overview.exportRow')}
                  onClick={() => onExport?.(employee)}
                >
                  <Download className="size-4" />
                </Button>
              </TableCell>
            </TableRow>
          )
          })}
        </LoaderTable>
      </TableBody>
    </Table>
  )
}
